import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Brain, Monitor, Smile, CreditCard, Shield, Users, 
  Headphones, MapPin, DollarSign, ChevronDown, Menu, X,
  Star, Phone, Mail, Building2, Home, Heart, Pill, Stethoscope,
  Hospital, Package, ClipboardList
} from 'lucide-react';

const PublicLandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    agencyName: '',
    source: '',
    email: '',
    phone: '',
    interest: 'Initiating a Referral'
  });

  const features = [
    { icon: Brain, title: 'AI-powered Intelligence', desc: 'Smart automation for clinical documentation and operational workflows' },
    { icon: Monitor, title: 'Complete Digital Workflow', desc: 'Manage your entire agency from anywhere, at any time' },
    { icon: Smile, title: 'Simple and Intuitive', desc: 'User-friendly interface that requires minimal training' },
    { icon: CreditCard, title: 'Universal Billing Support', desc: 'Bill Medicare, Medicaid, Insurance, VA, CIGNA, Blue Cross Blue Shield, all major commercial insurance and Private Pay' },
    { icon: Shield, title: 'Compliance Ready', desc: 'Stay compliant with CMS and state regulations automatically' },
    { icon: Users, title: 'Multi-Service Support', desc: 'Handle skilled and non-skilled care in one unified system' },
    { icon: Headphones, title: 'Expert Support Team', desc: 'Live support from healthcare software experts, available instantly' },
    { icon: MapPin, title: 'Built-in EVV', desc: 'Electronic Visit Verification integrated seamlessly into workflows' },
    { icon: DollarSign, title: 'Competitive Pricing', desc: 'Enterprise features at prices that work for agencies of all sizes' },
  ];

  const testimonials = [
    {
      text: "The care I receive from Ivory Home Health has been exceptional. My nurse visits are always on time, and they truly listen to my concerns. I feel safer knowing they're just a call away.",
      author: "Margaret Williams",
      role: "Home Health Patient",
      company: "Long Beach, CA"
    },
    {
      text: "After my hip surgery, Ivory's physical therapy team helped me regain my mobility faster than I expected. Their compassionate approach made all the difference in my recovery.",
      author: "Robert Chen",
      role: "Rehabilitation Patient",
      company: "Signal Hill, CA"
    },
    {
      text: "My mother has been receiving hospice care from Ivory, and the team has been nothing short of angels. They treat her with dignity and keep our whole family informed and supported.",
      author: "Maria Santos",
      role: "Family Member",
      company: "Carson, CA"
    }
  ];

  const serviceCategories = [
    { name: 'Home Health', icon: Home, color: 'bg-green-500', href: '/login' },
    { name: 'Home Care', icon: Users, color: 'bg-purple-500', href: '/login' },
    { name: 'Hospice', icon: Heart, color: 'bg-blue-500', href: '/login' },
    { name: 'Clinic', icon: Building2, color: 'bg-teal-500', href: '/dashboard/clinic' },
    { name: 'Behavioral Health', icon: Brain, color: 'bg-orange-500', href: '/dashboard/behavioral-health' },
    { name: 'DME', icon: Pill, color: 'bg-amber-500', href: '/login' },
    { name: 'Case Management', icon: Shield, color: 'bg-red-500', href: '/login' },
    { name: 'Concierge Visiting Doctors', icon: Stethoscope, color: 'bg-purple-600', href: '/concierge' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    window.location.href = 'https://ivory-hhc.com';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
              <img src="/images/ivory-logo.png" alt="Ivory Medical Home" className="h-20 w-20 object-contain ivory-logo-spin" />
              <span className="text-2xl font-bold text-orange-500 italic">IVORY</span>
              <span className="text-gray-500">Medical Home</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <div className="relative">
                <button 
                  onClick={() => setSolutionsOpen(!solutionsOpen)}
                  className="flex items-center gap-1 text-gray-700 hover:text-orange-500"
                >
                  Solutions <ChevronDown className="w-4 h-4" />
                </button>
                {solutionsOpen && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                    <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-50">Home Health Software</a>
                    <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-50">Home Care Software</a>
                    <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-50">Hospice Software</a>
                  </div>
                )}
              </div>
              <a href="#about" className="text-gray-700 hover:text-orange-500">About</a>
              <a href="#careers" className="text-gray-700 hover:text-orange-500">Career</a>
              <a href="#contact" className="text-gray-700 hover:text-orange-500">Contact</a>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login" className="px-4 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50">
                Login
              </Link>
              <a href="#contact" className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                Send Referrals
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t">
              <nav className="flex flex-col gap-4">
                <a href="#" className="text-gray-700">Solutions</a>
                <a href="#about" className="text-gray-700">About</a>
                <a href="#careers" className="text-gray-700">Career</a>
                <a href="#contact" className="text-gray-700">Contact</a>
                <Link to="/login" className="text-orange-500">Login</Link>
                <a href="#contact" className="bg-orange-500 text-white px-4 py-2 rounded-lg text-center">Send Referrals</a>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-8">
                IVORY MEDICAL HOME IS an all-in-one AI-enabled EMR + management system{' '}
                <span className="text-green-300">for a Medical Home model of care.</span>
              </h1>
              <p className="text-lg text-orange-100 mb-8">
                It is an All-in-one operational intelligence Platform built to function as an in-house, ecosystems, engineered to unify and streamline the full continuum of services after hospitalization.
              </p>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop"
                alt="Healthcare Professional"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Operational Backbone Section */}
      <section className="py-16 lg:py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-6">
            The Operational Backbone of Modern Healthcare
          </h2>
          <p className="text-gray-300 text-center max-w-4xl mx-auto mb-12">
            The system delivers smart automation, clinical intelligence, operational oversight, and seamless coordination across all service lines. By embedding AI into documentation, scheduling, care planning, compliance, billing, communication, and quality monitoring, Ivory Health Medical Home Inc. ensures that every team—clinical, administrative, and operational—works from a single, connected source of truth.
          </p>
          
          {/* Care at Home Model - Service Buttons */}
          <h3 className="text-xl font-semibold text-center mb-8 text-orange-400">
            Our Care at Home Model of Care
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-12">
            {[
              { icon: Home, title: 'Home Health', href: '/login' },
              { icon: Users, title: 'Home Care', href: '/login' },
              { icon: Heart, title: 'Hospice', href: '/login' },
              { icon: Hospital, title: 'Clinic', href: '/login' },
              { icon: Brain, title: 'Behavioral Health', href: '/login' },
              { icon: Package, title: 'DME', href: '/login' },
              { icon: ClipboardList, title: 'Case Management', href: '/login' },
              { icon: Stethoscope, title: 'Concierge Visiting Doctors', href: '/login' },
            ].map((service, idx) => (
              <Link 
                key={idx} 
                to={service.href}
                className="bg-black border-2 border-yellow-500 rounded-xl p-4 text-center hover:bg-gray-900 transition-all transform hover:scale-105 shadow-lg"
              >
                <div className="w-10 h-10 border border-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <service.icon className="w-5 h-5 text-yellow-500" />
                </div>
                <h4 className="font-semibold text-yellow-500 text-xs leading-tight">{service.title}</h4>
              </Link>
            ))}
          </div>
          
          <p className="text-center text-orange-300 font-medium text-lg italic">
            It is not just a software; it is the operational backbone of a modern, intelligent, community-centered health system.
          </p>
        </div>
      </section>

      {/* Promise Section */}
      <section className="bg-gradient-to-r from-orange-500 to-orange-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">
            THIS IS <span className="text-green-300">OUR PROMISE:</span>
          </h2>
          <p className="text-xl text-white/90 mb-6 italic">
            Make it easier for your agency to run better.
          </p>
          <div className="flex justify-center gap-4">
            <a href="#contact" className="px-6 py-3 bg-white text-orange-500 rounded-lg font-semibold hover:bg-orange-50">
              Send Referrals
            </a>
            <a href="#contact" className="px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            What Our Customers Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-4">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-orange-500">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                  <p className="text-sm text-gray-500">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-16 lg:py-24 bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-orange-500 mb-2">
            Ready to learn more?
          </h2>
          <p className="text-center text-gray-400 mb-8">
            Get a personalized demo, pricing information, or answers to your questions.
          </p>
          <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-lg">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input 
                  type="text" 
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input 
                  type="text" 
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Source *</label>
              <input 
                type="text" 
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                value={formData.source}
                onChange={(e) => setFormData({...formData, source: e.target.value})}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input 
                  type="email" 
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input 
                  type="tel" 
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">I'm Interested In *</label>
              <select 
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                value={formData.interest}
                onChange={(e) => setFormData({...formData, interest: e.target.value})}
              >
                <option>Initiating a Referral</option>
                <option>Pricing Information</option>
                <option>Technical Support</option>
                <option>Partnership</option>
              </select>
            </div>
            <button 
              type="submit"
              className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
            >
              Submit Request
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">IVORY</h3>
              <p className="text-gray-400 text-sm mb-4">Home Health Care and Medical Home</p>
              <p className="text-gray-400 text-sm">3605 Long Beach Blvd, Suite 531<br/>Long Beach, CA 90807</p>
              <div className="mt-4 space-y-2 text-sm text-gray-400">
                <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> (562) 349-0860</p>
                <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> (562) 285-7216</p>
                <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> info@ivory_hhc.com</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-orange-400">Home Health Software</a></li>
                <li><a href="#" className="hover:text-orange-400">Home Care Software</a></li>
                <li><a href="#" className="hover:text-orange-400">Hospice Software</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-orange-400">About Us</a></li>
                <li><a href="#" className="hover:text-orange-400">Career</a></li>
                <li><a href="#" className="hover:text-orange-400">Contact</a></li>
                <li><Link to="/login" className="hover:text-orange-400">Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-orange-400">Documentation</a></li>
                <li><a href="#" className="hover:text-orange-400">Support Center</a></li>
                <li><a href="#" className="hover:text-orange-400">FAQs</a></li>
                <li><a href="#" className="hover:text-orange-400">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-500">
            <p>© 2025 Ivory Home Health Care and Medical Home. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLandingPage;
